precision highp float;

uniform vec2 iResolution;
uniform float iTime;
uniform vec2 iMouse;
uniform float lowFreq;
uniform float midFreq;
uniform float highFreq;
uniform bool isPlaying;
uniform float transitionFactor;
uniform float lineStraightness;
uniform float idleAnimation;
uniform float idleWaveHeight;

// Enhanced kick/beat detection
uniform float kickEnergy;
uniform float beatPhase;
uniform float bounceEffect;

// Settings uniforms
uniform float baseSpeed;
uniform float idleSpeed;
uniform float bassReactivity;
uniform float midReactivity;
uniform float highReactivity;
uniform float kickReactivity;
uniform float bounceIntensity;
uniform float waveIntensity;
uniform float waveComplexity;
uniform float rippleIntensity;
uniform float lineThickness;

// Grain uniforms
uniform bool enableGrain;
uniform float grainIntensity;
uniform float grainSpeed;
uniform float grainMean;
uniform float grainVariance;
uniform int grainBlendMode;

// Color uniforms
uniform vec3 bgColorDown;
uniform vec3 bgColorUp;
uniform vec3 color1In;
uniform vec3 color1Out;
uniform vec3 color2In;
uniform vec3 color2Out;
uniform vec3 color3In;
uniform vec3 color3Out;

varying vec2 vUv;

// Helper function to square a value
float squared(float value) {
  return value * value;
}

// Helper function for smoother step
float smootherstep(float edge0, float edge1, float x) {
  float t = clamp((x - edge0) / (edge1 - edge0), 0.0, 1.0);
  return t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
}

// Grain functions
vec3 channel_mix(vec3 a, vec3 b, vec3 w) {
  return vec3(mix(a.r, b.r, w.r), mix(a.g, b.g, w.g), mix(a.b, b.b, w.b));
}

float gaussian(float z, float u, float o) {
  return (1.0 / (o * sqrt(2.0 * 3.1415))) * exp(-(((z - u) * (z - u)) / (2.0 * (o * o))));
}

vec3 screen(vec3 a, vec3 b, float w) {
  return mix(a, vec3(1.0) - (vec3(1.0) - a) * (vec3(1.0) - b), w);
}

vec3 overlay(vec3 a, vec3 b, float w) {
  return mix(a, channel_mix(
    2.0 * a * b,
    vec3(1.0) - 2.0 * (vec3(1.0) - a) * (vec3(1.0) - b),
    step(vec3(0.5), a)
  ), w);
}

vec3 soft_light(vec3 a, vec3 b, float w) {
  return mix(a, pow(a, pow(vec3(2.0), 2.0 * (vec3(0.5) - b))), w);
}

// Apply grain to the color
vec3 applyGrain(vec3 color, vec2 uv) {
  if (!enableGrain) return color;
  
  float t = iTime * grainSpeed;
  float seed = dot(uv, vec2(12.9898, 78.233));
  float noise = fract(sin(seed) * 43758.5453 + t);
  noise = gaussian(noise, grainMean, grainVariance * grainVariance);
  
  vec3 grain = vec3(noise) * (1.0 - color);
  
  if (grainBlendMode == 0) {
    // Addition
    color += grain * grainIntensity;
  } else if (grainBlendMode == 1) {
    // Screen
    color = screen(color, grain, grainIntensity);
  } else if (grainBlendMode == 2) {
    // Overlay
    color = overlay(color, grain, grainIntensity);
  } else if (grainBlendMode == 3) {
    // Soft Light
    color = soft_light(color, grain, grainIntensity);
  } else if (grainBlendMode == 4) {
    // Lighten-Only
    color = max(color, grain * grainIntensity);
  }
  
  return color;
}

// Circular ripple effect for kicks - more subtle
float kickRipple(vec2 uv, float energy, float time) {
  float dist = distance(uv, vec2(0.5, 0.5));
  float width = 0.05; // Ripple width
  float speed = 1.2;  // Ripple expansion speed
  
  // Multiple ripples with different phases
  float ripple1 = smootherstep(energy * speed * time - width, energy * speed * time, dist);
  ripple1 *= smootherstep(dist, dist + width, energy * speed * time + width);
  
  float ripple2 = smootherstep(energy * speed * (time - 0.2) - width, energy * speed * (time - 0.2), dist);
  ripple2 *= smootherstep(dist, dist + width, energy * speed * (time - 0.2) + width);
  
  return (ripple1 + ripple2 * 0.5) * energy * 0.7; // More subtle
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  // Coords
  vec2 p = fragCoord.xy / iResolution.xy;
  
  // Background
  vec3 bgCol = mix(bgColorDown, bgColorUp, clamp(p.y * 2.0, 0.0, 1.0));
  
  // IMPROVED TRANSITION: Blend between idle and playing states
  // Base movement speed - smooth transition between playing and idle
  float speed = mix(idleSpeed, baseSpeed, transitionFactor);
  
  // Ball visibility - smooth transition
  float ballVisibility = mix(0.8, 0.2, transitionFactor);
  
  // Calculate straightness factor - higher value = straighter lines when music is playing
  float straightnessFactor = mix(1.0, lineStraightness, transitionFactor);
  
  // IDLE ANIMATION: Create gentle wave motion when music is not playing
  float idleWave = idleWaveHeight * sin(p.x * 5.0 + idleAnimation * 0.2);
  
  // Use squared function for more impact but with moderation
  float bassPulse = squared(lowFreq) * bassReactivity * transitionFactor;
  float midPulse = squared(midFreq) * midReactivity * transitionFactor;
  float highPulse = squared(highFreq) * highReactivity * transitionFactor;
  
  // More moderate kick response
  float kickPulse = squared(kickEnergy) * kickReactivity * 1.5 * transitionFactor; 
  
  // Global vertical bounce effect on kicks - more subtle
  float bounce = bounceEffect * bounceIntensity * transitionFactor;
  
  // Add audio reactivity to the curve - acts like an audio wave
  // IMPROVED TRANSITION: Blend between idle wave and audio reactive wave
  float curveIntensity = mix(idleWaveHeight, 0.05 + waveIntensity * (bassPulse + kickPulse * 0.7), transitionFactor);
  float curveSpeed = speed;
  
  // Main curve that acts like an audio wave
  float curve = curveIntensity * sin((6.25 * p.x) + (curveSpeed * iTime));
  
  // Add kick ripple effect - more subtle
  float ripple = rippleIntensity * kickRipple(p, kickEnergy, mod(iTime, 10.0)) * transitionFactor;
  
  // IMPROVED TRANSITION: Audio wave intensity tied to transition factor
  float audioWave = mix(
    0.0, // No distortion when idle
    (0.1 * sin(p.x * 20.0 * waveComplexity) * bassPulse + 
     0.08 * sin(p.x * 30.0 * waveComplexity) * midPulse + 
     0.05 * sin(p.x * 50.0 * waveComplexity) * highPulse) / straightnessFactor,
    transitionFactor
  );
  
  // Line A (Bass/Kick) - dedicated to kick visualization
  float lineAFreq = 40.0 * waveComplexity + 80.0 * bassPulse + 90.0 * kickPulse;
  float lineASpeed = 1.5 * speed + 6.0 * bassPulse + 6.0 * kickPulse;
  
  // IMPROVED TRANSITION: Blend between idle and reactive wave
  float lineAWave = mix(
    idleWave,
    (0.01 + 0.05 * bassPulse + 0.1 * kickPulse) / straightnessFactor,
    transitionFactor
  );
  
  // More subtle kick wave effect
  float kickWaveEffect = 0.0;
  if (kickEnergy > 0.1) {
    // Create a traveling wave distortion when kick hits - more subtle
    kickWaveEffect = kickEnergy * 0.3 * sin(15.0 * (p.x - iTime * 0.5)) * transitionFactor;
  }
  
  // Apply bass effect with moderation
  float lineAOffset = bassPulse * 0.3 * sin(p.x * 10.0 - iTime * 2.0) + kickWaveEffect * 0.7;
  
  // IMPROVED TRANSITION: Blend idle position with reactive position
  float lineAY = 0.5; // Center position
  float lineAActive = lineAY + curve + audioWave + lineAWave * sin((lineAFreq * p.x) + (-lineASpeed * iTime)) + lineAOffset - bounce;
  float lineAIdle = lineAY + idleWave;
  
  // Blend between idle and active positions
  float lineAAnim = mix(lineAIdle, lineAActive, transitionFactor);
  
  // Adjust line thickness with kick energy - more moderate
  float lineAThickness = lineThickness * (1.0 + bassPulse * 0.4 + kickPulse * 0.8);
  float lineADist = distance(p.y, lineAAnim) * (2.0 / lineAThickness);
  float lineAShape = smootherstep(1.0 - clamp(lineADist, 0.0, 1.0), 1.0, 0.99);
  
  // More subtle color enhancement
  vec3 kickColor = vec3(1.0, 0.7, 0.3); // Bright orange-yellow
  vec3 enhancedColor1In = mix(color1In, kickColor, kickEnergy * 0.6 * transitionFactor);
  vec3 enhancedColor1Out = mix(color1Out, vec3(1.0, 0.5, 0.0), kickEnergy * 0.4 * transitionFactor);
  vec3 lineACol = (1.0 - lineAShape) * vec3(mix(enhancedColor1In, enhancedColor1Out, lineAShape));
  
  // Ball A - follows line A exactly - more moderate growth on kicks
  float ballASize = 0.5 + 0.4 * bassPulse + kickEnergy * 1.2 * transitionFactor;
  float ballAX = 0.2 + 0.1 * sin(iTime * 0.2 * speed) * midPulse;
  float ballADist = distance(p, vec2(ballAX, lineAAnim));
  float ballAShape = smootherstep(1.0 - clamp(ballADist * ballASize, 0.0, 1.0), 1.0, 0.99);
  vec3 ballACol = (1.0 - ballAShape) * vec3(mix(enhancedColor1In, enhancedColor1Out, ballAShape)) * mix(1.0, ballVisibility, transitionFactor);
  
  // Line B (Mid) - reacts to mid frequencies and slightly to kicks
  float lineBFreq = 50.0 * waveComplexity + 100.0 * midPulse;
  float lineBSpeed = 2.0 * speed + 8.0 * midPulse;
  
  // IMPROVED TRANSITION: Blend between idle and reactive wave
  float lineBWave = mix(
    idleWave * 0.8, // Slightly different idle wave
    (0.01 + 0.05 * midPulse) / straightnessFactor,
    transitionFactor
  );
  
  // Apply mid-frequency effect with minimal kick influence
  float lineBOffset = midPulse * 0.2 * sin(p.x * 15.0 - iTime * 1.5) + kickEnergy * 0.1 * sin(p.x * 25.0 - iTime * 3.0) * transitionFactor;
  
  // IMPROVED TRANSITION: Blend idle position with reactive position
  float lineBY = 0.5; // Center position
  float lineBActive = lineBY + curve - audioWave + lineBWave * sin((lineBFreq * p.x) + (lineBSpeed * iTime)) * sin(lineBSpeed * iTime) + lineBOffset - bounce * 0.5;
  float lineBIdle = lineBY + idleWave * 0.8;
  
  // Blend between idle and active positions
  float lineBAnim = mix(lineBIdle, lineBActive, transitionFactor);
  
  // Adjust line thickness with mid response and minimal kick influence
  float lineBThickness = lineThickness * (1.0 + midPulse * 0.3 + kickEnergy * 0.3 * transitionFactor);
  float lineBDist = distance(p.y, lineBAnim) * (2.0 / lineBThickness);
  float lineBShape = smootherstep(1.0 - clamp(lineBDist, 0.0, 1.0), 1.0, 0.99);
  
  // Slightly enhance mid colors with kick
  vec3 enhancedColor2In = mix(color2In, vec3(1.0, 0.5, 0.5), kickEnergy * 0.3 * transitionFactor);
  vec3 lineBCol = (1.0 - lineBShape) * vec3(mix(enhancedColor2In, color2Out, lineBShape));
  
  // Ball B - follows line B exactly with minimal kick influence
  float ballBSize = 0.5 + 0.4 * highPulse + kickEnergy * 0.3 * transitionFactor;
  float ballBX = 0.8 - 0.1 * sin(iTime * 0.3 * speed) * midPulse;
  float ballBDist = distance(p, vec2(ballBX, lineBAnim));
  float ballBShape = smootherstep(1.0 - clamp(ballBDist * ballBSize, 0.0, 1.0), 1.0, 0.99);
  vec3 ballBCol = (1.0 - ballBShape) * vec3(mix(enhancedColor2In, color2Out, ballBShape)) * mix(1.0, ballVisibility, transitionFactor);
  
  // Line C (High) - minimal kick influence
  float lineCFreq = 60.0 * waveComplexity + 120.0 * highPulse;
  float lineCSpeed = 2.5 * speed + 10.0 * highPulse;
  
  // IMPROVED TRANSITION: Blend between idle and reactive wave
  float lineCWave = mix(
    idleWave * 1.2, // Different idle wave
    (0.01 + 0.05 * highPulse) / straightnessFactor,
    transitionFactor
  );
  
  // Apply high-frequency effect with minimal kick influence
  float lineCOffset = highPulse * 0.15 * sin(p.x * 20.0 - iTime * 1.0);
  
  // IMPROVED TRANSITION: Blend idle position with reactive position
  float lineCY = 0.5; // Center position
  float lineCActive = lineCY + curve * 0.7 - audioWave * 0.5 + lineCWave * sin((lineCFreq * p.x) + (lineCSpeed * iTime)) * sin(lineCSpeed * (iTime + 0.1)) + lineCOffset - bounce * 0.3;
  float lineCIdle = lineCY + idleWave * 1.2;
  
  // Blend between idle and active positions
  float lineCAnim = mix(lineCIdle, lineCActive, transitionFactor);
  
  // Adjust line thickness with high response and minimal kick influence
  float lineCThickness = lineThickness * (1.0 + highPulse * 0.2 + kickEnergy * 0.1 * transitionFactor);
  float lineCDist = distance(p.y, lineCAnim) * (2.0 / lineCThickness);
  float lineCShape = smootherstep(1.0 - clamp(lineCDist, 0.0, 1.0), 1.0, 0.99);
  vec3 lineCCol = (1.0 - lineCShape) * vec3(mix(color3In, color3Out, lineCShape));
  
  // Ball C - follows line C exactly with minimal kick influence
  float ballCSize = 0.5 + 0.4 * highPulse + kickEnergy * 0.1 * transitionFactor;
  float ballCX = 0.5 + 0.15 * sin(iTime * 0.4 * speed) * highPulse;
  float ballCDist = distance(p, vec2(ballCX, lineCAnim));
  float ballCShape = smootherstep(1.0 - clamp(ballCDist * ballCSize, 0.0, 1.0), 1.0, 0.99);
  vec3 ballCCol = (1.0 - ballCShape) * vec3(mix(color3In, color3Out, ballCShape)) * mix(1.0, ballVisibility, transitionFactor);
  
  // Add subtle kick flash to background
  bgCol = mix(bgCol, mix(bgCol, vec3(1.0), 0.2), kickEnergy * 0.4 * transitionFactor);
  
  // Add ripple effect - more subtle
  vec3 rippleCol = vec3(1.0, 0.8, 0.4) * ripple * transitionFactor;
  
  // Final color
  vec3 fcolor = bgCol + lineACol + lineBCol + lineCCol + ballACol + ballBCol + ballCCol + rippleCol;
  
  // Apply grain effect
  fcolor = applyGrain(fcolor, p);
  
  fragColor = vec4(fcolor, 1.0);
}

void main() {
  vec2 fragCoord = vUv * iResolution;
  vec4 fragColor;
  mainImage(fragColor, fragCoord);
  gl_FragColor = fragColor;
}
